import React, { useMemo } from 'react';
import { TextField, Edit, SimpleForm, TextInput, usePermissions, useRecordContext, useTranslate, FunctionField } from 'react-admin';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import PropTypes from 'prop-types';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { useDrawer } from '@/composables/useDrawer';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { OnlyBulkDeleteActionButton } from '@/components/global/buttons/bulkButtons.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';

const getFilters = (isMobile, translate) => [<TextInput key='name' source='name' label={translate('common.name')} alwaysOn={isMobile} />];

const MoreActionsPopover = ({ permissions }) => {
    const { openDrawer } = useDrawer();
    const record = useRecordContext();
    const translate = useTranslate();
    const canShow = canViewDetails('material', permissions);

    if (!record) return null;
    if (!canShow && !permissions.isAdmin) return null;

    return (
        <CustomMorePopover record={record}>
            {canShow && (
                <CustomShowModalButton label={translate('resources.material.labels.showMaterial')} onOpen={() => openDrawer(record.id, 'material-show')} />
            )}
            {permissions.isAdmin && (
                <CustomEditModalButton label={translate('resources.material.labels.editMaterial')} onOpen={() => openDrawer(record.id, 'material-edit')} />
            )}
        </CustomMorePopover>
    );
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isBok: PropTypes.bool,
    }),
};

export const MaterialList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);
    const canShow = canViewDetails('material', permissions);

    if (isLoading) return null;

    if (permissions.isAdmin || permissions.isBok) {
        return (
            <CustomList filters={filters}>
                <CustomDatagridConfigurable
                    rowClick={
                        canShow
                            ? id => {
                                  openDrawer(id, 'material-show');
                                  return false;
                              }
                            : false
                    }
                    bulkActionButtons={permissions.isAdmin ? <OnlyBulkDeleteActionButton /> : false}
                >
                    <TextField source='id' label={translate('resources.contractor.fields.id')} />
                    <TextField source='name' label={translate('common.name')} />
                    <FunctionField
                        label={translate('common.options')}
                        render={() => (
                            <Box onClick={event => event.stopPropagation()}>
                                <MoreActionsPopover permissions={permissions} />
                            </Box>
                        )}
                    />
                </CustomDatagridConfigurable>
            </CustomList>
        );
    }

    if (permissions.isClient) {
        return (
            <CustomList filters={filters}>
                <CustomDatagridConfigurable bulkActionButtons={false} rowClick={false}>
                    <TextField source='id' label={translate('resources.contractor.fields.id')} />
                    <TextField source='name' label={translate('common.name')} />
                </CustomDatagridConfigurable>
            </CustomList>
        );
    }
};

export const MaterialEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.material.labels.editMaterial')}>
            <Edit id={elementId} resource='material' mutationMode='pessimistic' redirect={false} actions={false}>
                <SimpleForm toolbar={<EditToolbar />}>
                    <TextInput source='id' disabled fullWidth={true} label={translate('resources.contractor.fields.id')} />
                    <TextInput source='name' fullWidth={true} label={translate('common.name')} />
                </SimpleForm>
            </Edit>
        </RightDrawer>
    );
};

MaterialEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
